import { useState, useEffect } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase, useAuthUid } from '@/App';
import { useLocalSession } from '@hooks/useLocalSession';
import { useEntitlements, FREE_TIER_MAX_PLAYERS } from '@hooks/useEntitlements';
import { trackEvent } from '@/firebase';
import { sanitiseNickname, sanitiseRoomName } from '@utils/validation';
import { assignMemberColour } from '@utils/colours';
import { generateRoomCode, validateRoomCode } from '@utils/roomCode';
import CreateRoomForm from '@components/CreateRoomForm';
import JoinRoomForm from '@components/JoinRoomForm';
import { Palette, ArrowRight, Clock, X, Trophy, RefreshCw, Scissors, MessageCircle, Sparkles } from 'lucide-react';

function formatTimeAgo(timestamp) {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function LandingScreen() {
  const { state, dispatch } = useApp();
  const authUid = useAuthUid();
  const session = useLocalSession(authUid);
  const firebase = useFirebase();
  const entitlements = useEntitlements();
  const [tab, setTab] = useState('join');
  const [nickname, setNickname] = useState(session.nickname);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check URL for room code on mount (e.g. /app/room/DRAGON or /room/DRAGON)
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/(?:\/app)?\/room\/([A-Z0-9]{6,8})$/i);
    if (match && nickname.trim()) {
      const code = match[1].toUpperCase();
      enterRoom(code, '', false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enterRoom = async (code, roomName, isHost) => {
    const cleanNickname = sanitiseNickname(nickname);
    if (!cleanNickname) {
      setError('Please enter a nickname (letters, numbers, spaces, hyphens)');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const colour = assignMemberColour([]);
      const cleanRoomName = sanitiseRoomName(roomName);

      if (isHost) {
        // Bake the host's Host Pass entitlement into the room metadata so
        // joining players can check it without needing to query the host.
        await firebase.createRoom(
          code,
          cleanRoomName,
          session.userId,
          cleanNickname,
          colour,
          entitlements.hasHostPass,
        );
      } else {
        const status = await firebase.getRoomStatus(code);
        if (!status.exists) {
          setError('Room not found — it may have expired');
          setLoading(false);
          return;
        }
        if (status.locked) {
          setError('This room is currently locked by the host');
          setLoading(false);
          return;
        }
        if (status.full) {
          setError('This room is full (8/8)');
          setLoading(false);
          return;
        }
        // Free-tier block: if the host doesn't have Host Pass, the room
        // is capped at 3 players. The joining player is told to ask the
        // host to upgrade.
        if (!status.hostHasHostPass && status.memberCount >= FREE_TIER_MAX_PLAYERS) {
          setError(`This workshop is full. The host needs Host Pass to let ${FREE_TIER_MAX_PLAYERS + 1}+ friends join.`);
          setLoading(false);
          return;
        }
        await firebase.joinRoom(code, session.userId, cleanNickname, colour);
        roomName = status.roomName;
      }

      firebase.setupDisconnect(code, session.userId);
      firebase.startListeners(code, session.userId);

      session.setNickname(cleanNickname);
      session.addRoomToHistory({ code, name: roomName });

      // Update URL without reload
      window.history.pushState(null, '', `/app/room/${code}`);

      dispatch({ type: 'SET_USER', payload: { id: session.userId, nickname: cleanNickname, colour, isHost } });
      dispatch({ type: 'SET_ROOM_CODE', payload: code });
      dispatch({ type: 'SET_SCREEN', payload: 'whiteboard' });

      // Analytics
      trackEvent(isHost ? 'room_created' : 'room_joined', { room_code: code });
    } catch (err) {
      console.error('Error entering room:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (roomName) => {
    const code = generateRoomCode();
    enterRoom(code, roomName, true);
  };

  const handleJoin = (code) => {
    enterRoom(code, '', false);
  };

  const handleRejoin = (code) => {
    enterRoom(code, '', false);
  };

  return (
    <div className="h-full overflow-y-auto bg-canvas">
      <div className="max-w-md mx-auto p-4 py-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Palette className="w-10 h-10 text-amber" />
            <h1 className="text-4xl font-bold font-display text-charcoal">DrawLab</h1>
          </div>
          <p className="text-slate-500 font-display">Collaborative whiteboard for creative groups</p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          {/* Nickname */}
          <div className="p-6 border-b border-slate-100">
            <label className="block text-sm font-medium text-charcoal mb-2">Your nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="What should we call you?"
              maxLength={20}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-canvas text-charcoal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber/50 focus:border-amber font-display"
            />
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-100">
            <button
              onClick={() => setTab('join')}
              className={`flex-1 py-3 text-sm font-medium font-display transition-colors ${
                tab === 'join' ? 'text-amber border-b-2 border-amber' : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              Join a Room
            </button>
            <button
              onClick={() => setTab('create')}
              className={`flex-1 py-3 text-sm font-medium font-display transition-colors ${
                tab === 'create' ? 'text-amber border-b-2 border-amber' : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              Create a Room
            </button>
          </div>

          {/* Form */}
          <div className="p-6">
            {tab === 'join' ? (
              <JoinRoomForm onJoin={handleJoin} loading={loading} />
            ) : (
              <CreateRoomForm onCreate={handleCreate} loading={loading} />
            )}

            {error && (
              <p className="mt-4 text-sm text-red-500 text-center font-display">{error}</p>
            )}
          </div>
        </div>

        {/* Game modes feature callout */}
        <div className="mt-6 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-charcoal font-display">5 Game Modes</h3>
            <p className="text-xs text-slate-400 font-display mt-0.5">The host can start a game from inside any room — each one rooted in real art history</p>
          </div>
          <div className="grid grid-cols-1 divide-y divide-slate-100">
            <div className="grid grid-cols-2 divide-x divide-slate-100">
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Trophy className="w-3.5 h-3.5 text-amber" />
                  <span className="text-xs font-semibold text-charcoal font-display">Challenge</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">Same prompt, solo canvases, vote for the best</p>
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <RefreshCw className="w-3.5 h-3.5 text-amber" />
                  <span className="text-xs font-semibold text-charcoal font-display">Relay</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">Take turns on one canvas. Try Blind Relay!</p>
              </div>
            </div>
            <div className="grid grid-cols-3 divide-x divide-slate-100">
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Scissors className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-xs font-semibold text-charcoal font-display">Corpse</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">Head, body, legs — Surrealist classic</p>
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <MessageCircle className="w-3.5 h-3.5 text-sky-500" />
                  <span className="text-xs font-semibold text-charcoal font-display">Telephone</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">Draw, guess, draw — watch it drift</p>
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-xs font-semibold text-charcoal font-display">Squiggle</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">Scribble + transform — Winnicott's game</p>
              </div>
            </div>
          </div>
        </div>

        {/* Room history */}
        {session.roomHistory.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-charcoal font-display mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              Your Rooms
            </h3>
            <div className="space-y-2">
              {session.roomHistory.map((room) => (
                <div
                  key={room.code}
                  className="flex items-center justify-between px-4 py-3 bg-white rounded-xl border border-slate-100 hover:border-amber/30 transition-colors group"
                >
                  <button
                    onClick={() => handleRejoin(room.code)}
                    className="flex-1 flex items-center gap-3 text-left min-w-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal font-display truncate">
                        {room.name || 'Unnamed Room'}
                      </p>
                      <p className="text-[11px] text-slate-400 font-display">
                        {formatTimeAgo(room.lastVisited)}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-xs font-mono text-amber bg-amber/10 px-2 py-1 rounded">
                      {room.code}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        session.removeRoomFromHistory(room.code);
                      }}
                      title="Remove from history"
                      className="w-6 h-6 flex items-center justify-center rounded text-slate-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
